/* eslint-disable react/display-name */
import React, { forwardRef, ReactNode, useEffect, useState } from 'react';
import Tippy from '@tippyjs/react/headless';
import { followCursor as followCursorPlugin, Placement } from 'tippy.js';

import { Text } from './components';
import { Container } from './Tooltip.styles';

import { Mode } from './types';
import { KEYBOARD_KEYS } from '../../../constants';

export interface TooltipProps {
  /**
   * Tooltip mode
   */
  mode?: Mode;
  /**
   * Tooltip render content function
   */
  renderContent: () => React.ReactNode;
  /**
   * Determines if the tooltip follows the cursor
   */
  followCursor?: boolean | 'horizontal' | 'vertical' | 'initial';
  /**
   * Determines if the tooltip has interactive content inside of it, so that it can be hovered over and clicked inside without hiding.
   */
  interactive?: boolean;
  /**
   * Preferred tooltip position
   */
  placement?: Placement;
  /**
   * Reference element
   */
  children: ReactNode;
}

/**
- The Tooltip component allows to easily display informative text when users hover over, focus on, or tap an element.
**/
const Tooltip = ({
  mode = 'light',
  followCursor = false,
  interactive = false,
  placement = 'top',
  children,
  renderContent,
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === KEYBOARD_KEYS.ESCAPE && visible) {
        setVisible(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visible]);

  const Wrapper = forwardRef<HTMLDivElement>((_props, ref) => (
    <div
      ref={ref}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onKeyDown={(e) => {
        if (e.key === KEYBOARD_KEYS.ENTER && !visible) setVisible(true);
      }}
      style={{ display: 'inline-block', lineHeight: 1, cursor: 'pointer' }}
    >
      {children}
    </div>
  ));

  return (
    <Tippy
      placement={placement}
      followCursor={followCursor}
      plugins={[followCursorPlugin]}
      interactive={interactive}
      visible={visible}
      render={(attrs) => (
        <Container mode={mode} size="regular" role="tooltip" {...attrs}>
          {renderContent()}
        </Container>
      )}
    >
      <Wrapper />
    </Tippy>
  );
};

export default Object.assign(Tooltip, {
  Text,
});
